import _ from 'lodash';

import { PresenceEntityType, PresenceUserStatus } from 'constants/presence';

export const normalizeUserId = id => (_.isNil(id) ? null : String(id).toLowerCase());

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// The presence hub reads the entity reference out of the existing first join/heartbeat/leave
// argument as `"{EntityType}:{Guid}"`, and a bare Guid still resolves to Loan.
//
// The id reaches here straight from the route (`useParams().loanId`), so it must be rejected
// unless it is a bare GUID: a crafted id that already contains the `:` delimiter would otherwise
// be forwarded verbatim and resolved by the hub as a DIFFERENT entity type's presence room.
//
// TODO(STP-456): loans send the bare id so this also works against a hub predating STP-456, which
// throws on the prefixed form. Drop the special case once that hub is live everywhere.
export const buildPresenceEntityRef = (entityType, entityId) => {
  if (_.isNil(entityId) || !GUID_PATTERN.test(String(entityId))) {
    return null;
  }

  return entityType === PresenceEntityType.LOAN ? entityId : `${entityType}:${entityId}`;
};

export const getIsUserActive = user => user?.status === PresenceUserStatus.ACTIVE;

export const getIsUserIdle = user => user?.status === PresenceUserStatus.IDLE;

const getPresenceUserKey = connection =>
  normalizeUserId(connection.userId) ?? connection.connectionId;

const compareConnections = (a, b) => {
  const aIsActive = getIsUserActive(a);
  const bIsActive = getIsUserActive(b);

  if (aIsActive !== bIsActive) {
    return aIsActive ? -1 : 1;
  }

  const aTime = new Date(a.lastActivityAt).getTime();
  const bTime = new Date(b.lastActivityAt).getTime();
  const aHasTime = !Number.isNaN(aTime);
  const bHasTime = !Number.isNaN(bTime);

  if (aHasTime !== bHasTime) {
    return aHasTime ? -1 : 1;
  }

  if (aHasTime && bHasTime && aTime !== bTime) {
    return bTime - aTime;
  }

  return String(a.connectionId).localeCompare(String(b.connectionId));
};

export const collapseConnectionsByUser = (connections = []) => {
  const grouped = new Map();

  connections.forEach(connection => {
    const key = getPresenceUserKey(connection);
    const existing = grouped.get(key);

    if (existing) {
      existing.push(connection);

      return;
    }

    grouped.set(key, [connection]);
  });

  return Array.from(grouped.values()).map(userConnections => {
    const sortedConnections = [...userConnections].sort(compareConnections);

    return {
      ...sortedConnections[0],
      connectionCount: sortedConnections.length,
    };
  });
};

export const getOtherTabsLabel = connectionCount => {
  if (_.isNil(connectionCount) || connectionCount <= 1) {
    return null;
  }

  if (connectionCount === 2) {
    return 'Also in 1 other browser tab';
  }

  return `Also in ${connectionCount - 1} other browser tabs`;
};
